import React, { useState } from 'react';

import { useMutation, useQuery, useQueryCache } from 'react-query';
import { Toast } from 'src/common/components';
import { Icon } from 'src/common/components/Icon';
import { OrbitIcons } from 'src/common/constants.';

import {
  fetchRingStrategies,
  activateRingStrategy,
  deactivateRingStrategy,
  ALL_RING_STRATEGY_NAMES,
  RING_STRATEGY_DESCRIPTIONS,
} from '../api/ringStrategies';
import type { RingStrategy, RingStrategyName } from '../api/ringStrategies';

export const RingStrategies: React.FC = () => {
  const queryCache = useQueryCache();
  const [pendingAction, setPendingAction] = useState<string | null>(null);

  const { data, isLoading, refetch } = useQuery<RingStrategy[], Error>(
    'ring-strategies',
    fetchRingStrategies,
    {
      refetchOnWindowFocus: false,
      retry: 1,
      onError: () => {
        Toast.error('Error al cargar las estrategias de ring');
      },
    }
  );

  const activeStrategies = data ?? [];

  const [activate, { isLoading: isActivating }] = useMutation(activateRingStrategy, {
    onSuccess: (result) => {
      Toast.success(result.message);
      queryCache.invalidateQueries('ring-strategies');
      setPendingAction(null);
    },
    onError: (err: { response?: { data?: { detail?: string } } }) => {
      const detail = err?.response?.data?.detail;
      Toast.error(typeof detail === 'string' ? detail : 'Error al activar la estrategia');
      setPendingAction(null);
    },
  });

  const [deactivate, { isLoading: isDeactivating }] = useMutation(deactivateRingStrategy, {
    onSuccess: (result) => {
      Toast.success(result.message);
      queryCache.invalidateQueries('ring-strategies');
      setPendingAction(null);
    },
    onError: (err: { response?: { data?: { detail?: string } } }) => {
      const detail = err?.response?.data?.detail;
      Toast.error(typeof detail === 'string' ? detail : 'Error al desactivar la estrategia');
      setPendingAction(null);
    },
  });

  const handleActivate = (name: RingStrategyName): void => {
    setPendingAction(name);
    activate(name);
  };

  const handleDeactivate = (strategy: RingStrategy): void => {
    setPendingAction(strategy.name);
    deactivate(strategy.id);
  };

  const isProcessing = isActivating || isDeactivating;

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-white-2.0 bg-primary-2.0 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-lg font-bold text-white-0.0">Ring Strategies</h1>
              {isLoading ? (
                <p className="text-xs text-white-1.0">Cargando estrategias…</p>
              ) : (
                <p className="text-xs text-white-1.0">
                  {activeStrategies.length} de {ALL_RING_STRATEGY_NAMES.length} estrategias activas
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => refetch()}
              disabled={isProcessing}
              className="flex items-center gap-1 rounded-md bg-white/10 px-3 py-1.5 text-xs text-white-0.0 hover:bg-white/20 disabled:opacity-50"
              title="Actualizar lista"
            >
              <Icon name={OrbitIcons.RefreshCw} size={14} />
              Actualizar
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 p-6 overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Icon name={OrbitIcons.RefreshCw} size={24} className="animate-spin text-primary-2.0" />
            <span className="ml-3 text-gray-2.0">Cargando estrategias...</span>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 mb-6 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <Icon name={OrbitIcons.AlertCircle} size={14} className="inline mr-2" />
                Las estrategias de ring determinan cómo se distribuyen las llamadas entrantes entre
                los agentes. Activa las que necesites usar en tus módulos de llamada.
              </p>
            </div>

            <div className="md:grid-cols-2 grid gap-4">
              {ALL_RING_STRATEGY_NAMES.map((name) => {
                const activeStrategy = activeStrategies.find((s) => s.name === name);
                const isActive = !!activeStrategy;
                const isPending = pendingAction === name;

                return (
                  <div
                    key={name}
                    className={`rounded-lg border p-5 shadow-sm transition-colors ${
                      isActive
                        ? 'border-green-200 bg-green-50'
                        : 'border-white-2.0 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`flex h-10 w-10 items-center justify-center rounded-full ${
                          isActive ? 'bg-green-500' : 'bg-gray-200'
                        }`}
                      >
                        <Icon
                          name={isActive ? OrbitIcons.Check : OrbitIcons.Workflow}
                          size={20}
                          className={isActive ? 'text-white' : 'text-gray-500'}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-base font-semibold text-black-1.0">{name}</h3>
                          {isActive && (
                            <span className="rounded bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-700">
                              ACTIVA
                            </span>
                          )}
                          {activeStrategy && (
                            <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-[10px] text-gray-600">
                              ID: {activeStrategy.id}
                            </span>
                          )}
                        </div>
                        <p className="mt-2 text-sm text-gray-2.0">
                          {RING_STRATEGY_DESCRIPTIONS[name]}
                        </p>

                        <div className="mt-4">
                          {isActive ? (
                            <button
                              type="button"
                              onClick={() => handleDeactivate(activeStrategy)}
                              disabled={isProcessing}
                              className="flex items-center gap-1.5 rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                            >
                              {isPending ? (
                                <Icon
                                  name={OrbitIcons.RefreshCw}
                                  size={12}
                                  className="animate-spin"
                                />
                              ) : (
                                <Icon name={OrbitIcons.X} size={12} />
                              )}
                              Desactivar
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleActivate(name)}
                              disabled={isProcessing}
                              className="flex items-center gap-1.5 rounded-md border border-green-300 bg-white px-3 py-1.5 text-xs font-medium text-green-600 hover:bg-green-50 disabled:opacity-50"
                            >
                              {isPending ? (
                                <Icon
                                  name={OrbitIcons.RefreshCw}
                                  size={12}
                                  className="animate-spin"
                                />
                              ) : (
                                <Icon name={OrbitIcons.Check} size={12} />
                              )}
                              Activar
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="border-amber-200 bg-amber-50 p-4 mt-6 border rounded-lg">
              <p className="text-amber-800 text-sm">
                <Icon name={OrbitIcons.AlertCircle} size={14} className="inline mr-2" />
                No puedes desactivar una estrategia que esté siendo usada por algún módulo de
                llamada. Primero cambia la estrategia en los módulos que la usen.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
